import { motion } from "framer-motion";
import { Phone, Star, MapPin, Clock } from "lucide-react";
import { PRACTICE, IMAGES } from "@/data/practice";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  return (
    <section
      id="accueil"
      data-testid="hero-section"
      className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 overflow-hidden"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
        {/* Left: text */}
        <div className="relative z-10">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={0}
            className="inline-flex items-center gap-2 bg-white rounded-full pl-2 pr-4 py-1.5 shadow-sm border border-[#E0E0D8] mb-6"
          >
            <span className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-[#E8B04B] text-[#E8B04B]" strokeWidth={0} />
              ))}
            </span>
            <span className="text-sm font-medium text-[#2C362B]">
              {PRACTICE.rating} sur Google · {PRACTICE.reviewCount} avis
            </span>
          </motion.div>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="text-xs font-bold uppercase tracking-[0.2em] text-[#3F9E8E] mb-4"
          >
            Infirmiers libéraux à Bordeaux
          </motion.p>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#2C362B] leading-[1.05]"
          >
            Perfusions, pansements, prise en charge du diabète...
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-6 text-base sm:text-lg text-[#4A554A] leading-relaxed max-w-xl"
          >
            Soins infirmiers à domicile et au cabinet, dans les quartiers Victoire,
            Capucins, Saint-Jean et Saint-Michel. Une équipe humaine et réactive,
            à votre écoute 7j/7 de 8h à 20h.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <a
              href={`tel:${PRACTICE.phoneRaw}`}
              data-testid="hero-call-button"
              className="inline-flex items-center justify-center gap-2.5 rounded-full bg-[#3F9E8E] text-white font-medium text-base px-7 py-4 shadow-md hover:bg-[#348275] hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
            >
              <Phone className="w-5 h-5" strokeWidth={2} />
              Appeler le {PRACTICE.phone}
            </a>
            <a
              href="#services"
              data-testid="hero-services-link"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#E3E7E0] text-[#2C362B] font-medium text-base px-7 py-4 hover:bg-[#D1D6CD] transition-colors duration-200"
            >
              Voir nos soins
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={5}
            className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#4A554A]"
          >
            <span className="inline-flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#6B7A66]" strokeWidth={1.5} />
              {PRACTICE.address}
            </span>
            <span className="inline-flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#6B7A66]" strokeWidth={1.5} />
              {PRACTICE.hours}
            </span>
          </motion.div>
        </div>

        {/* Right: image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgb(0,0,0,0.12)] aspect-[4/5] max-w-md mx-auto lg:mx-0 lg:ml-auto">
            <img
              src={IMAGES.hero}
              alt="Infirmier prodiguant des soins à domicile à un patient à Bordeaux"
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2C362B]/30 via-transparent to-transparent" />
          </div>

          {/* Floating trust card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="absolute -bottom-5 -left-2 sm:left-4 bg-white/80 backdrop-blur-xl border border-white/50 rounded-2xl px-5 py-4 shadow-[0_12px_40px_rgb(0,0,0,0.1)] max-w-[15rem]"
          >
            <p className="font-display font-bold text-[#2C362B] text-base leading-tight">
              Conventionné Secteur 1
            </p>
            <p className="text-xs text-[#4A554A] mt-1">
              Soins remboursés · Tiers payant
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
